import { SetStateAction, useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * State of the reservation edit, counted in edit sessions. Every value set
 * opens, switches or closes the edit and starts a new session, also when the
 * same reservation is opened again; an updater only edits the open one. A new
 * restaurant or day and the unmount end the session too, right in their commit:
 * a PIN or write finishing before React's passive effects ran must already see
 * it. `beginEditSession()` returns whether the session it was called in is
 * still open.
 */
export function useEditSessionState<T>(initial: T, context: string) {
  const [value, replaceValue] = useState(initial);
  const session = useRef(0);

  const setValue = useCallback((next: SetStateAction<T>) => {
    if (typeof next !== 'function') session.current += 1;
    replaceValue(next);
  }, []);

  useLayoutEffect(
    () => () => {
      session.current += 1;
    },
    [context]
  );

  const beginEditSession = useCallback(() => {
    const started = session.current;
    return () => session.current === started;
  }, []);

  return [value, setValue, beginEditSession] as const;
}

/**
 * A table swap moves the other reservation right away and the edited one only
 * in its open edit (saved later). Started in one edit session: if that session
 * closed before the write (e.g. while the PIN was asked), nothing is written;
 * if it closed during the write, the other reservation stays moved and the
 * team is told, instead of changing whatever is edited now. No rollback: a
 * second write could fail just as well.
 */
export async function runTableSwap(swap: {
  stillOpen: () => boolean;
  write: () => Promise<unknown>;
  applyToEdit: () => void;
  onClosedMeanwhile: () => void;
  onError: (error?: unknown) => void;
}) {
  if (!swap.stillOpen()) return;
  try {
    await swap.write();
  } catch (error) {
    swap.onError(error);
    return;
  }
  if (swap.stillOpen()) swap.applyToEdit();
  else swap.onClosedMeanwhile();
}

/** Reports a write only once it is saved: success after it resolved, the error when it was refused. */
export const confirmWrite = (write: Promise<unknown>, onSaved: () => void, onRefused: () => void) =>
  write.then(onSaved, onRefused);
